import { IdentificationIcon } from '@heroicons/react/24/outline';
import type { CatalogRole } from '../../../services/iam';
import { shortRoleKey } from '../iamLookups';
import { PermCountPill } from '../permissions/PermCountPill';
import { AccessCard } from '../primitives/AccessCard';

// Same name treatment as RolesList's own row (role -> title, bundle -> key).
function IncludedByEntry({ parent, onSelect }: { parent: CatalogRole; onSelect: () => void }) {
    const isBundle = parent.kind === 'bundle';
    return (
        <button
            type="button"
            onClick={onSelect}
            className={`text-[11px] font-semibold px-2 py-1 rounded-md border border-gray-200 bg-white hover:bg-gray-50 transition-colors ${
                isBundle ? 'font-mono italic text-violet-700' : 'text-gray-700'
            }`}
        >
            {isBundle ? shortRoleKey(parent.key) : parent.title}
        </button>
    );
}

interface RoleOverviewCardProps {
    role: CatalogRole;
    includedBy: readonly CatalogRole[];
    routeCount: number;
    onSelectRole: (key: string) => void;
}

// Card 1: name, description, and permission/route counts in the action slot.
// No Role/Bundle or grantability badge - both already read off the tab and
// name styling, and grantability is constant (nothing's wired yet) today.
export function RoleOverviewCard({
    role,
    includedBy,
    routeCount,
    onSelectRole
}: RoleOverviewCardProps) {
    const isBundle = role.kind === 'bundle';

    return (
        <AccessCard
            icon={IdentificationIcon}
            title={
                isBundle ? (
                    <span className="font-mono italic text-violet-700">
                        {shortRoleKey(role.key)}
                    </span>
                ) : (
                    <span className="inline-flex items-baseline gap-2 min-w-0">
                        <span className="truncate">{role.title}</span>
                        <span className="font-mono text-[11px] font-normal text-gray-400 truncate">
                            {shortRoleKey(role.key)}
                        </span>
                    </span>
                )
            }
            action={
                <div className="flex items-center gap-1.5">
                    <PermCountPill noun="permission" count={role.flattened.length} />
                    <PermCountPill noun="route" count={routeCount} />
                </div>
            }
        >
            <p className="text-sm text-gray-600">{role.desc}</p>

            <div>
                <span className="block text-[11px] font-bold text-gray-400 uppercase tracking-[0.04em] mb-2">
                    Included by
                </span>
                {includedBy.length === 0 ? (
                    <p className="text-sm text-gray-400">
                        {isBundle
                            ? 'Nothing includes this bundle.'
                            : 'Nothing includes this role - most roles are granted directly, not composed into another.'}
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        {includedBy.map((parent) => (
                            <IncludedByEntry
                                key={parent.key}
                                parent={parent}
                                onSelect={() => onSelectRole(parent.key)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </AccessCard>
    );
}
